import { useState } from 'react';
import { UserRoles } from '@/enums/enums';
import { getUserOptions } from '@/features/user/user.queries';
import { toCapitalize } from '@/utils/text-utils';
import { createFileRoute, redirect, useNavigate } from '@tanstack/react-router';
import { zodSearchValidator } from '@tanstack/router-zod-adapter';
import { CalendarDays, Settings, TrendingUp } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { z } from 'zod';

import { Option } from '@/types/types';
import { Button } from '@/components/ui/button';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { OrganizationFilter } from '@/components/organization-filter';
import ContractViewSheet from '@/components/sheets/contract-sheet/contract-view-sheet';

import MonthTab from './-MonthTab';
import YearTab from './-YearTab';

const accountRouteSchema = z.object({
  mode: z.enum(['create', 'update', 'view']).optional(),
  expense: z.number().optional(),
});

export const Route = createFileRoute('/_app/(account)/account/')({
  validateSearch: zodSearchValidator(accountRouteSchema),
  component: Account,
  beforeLoad: async ({ context }) => {
    const { user } = await context.queryClient.ensureQueryData(getUserOptions);
    if (![UserRoles.Root, UserRoles.Admin].includes(user?.role as UserRoles)) {
      throw redirect({ to: '/' });
    }
  },
});

function Account() {
  const navigate = useNavigate();
  const { t } = useTranslation(['class']);
  const [selectedOrganizations, setSelectedOrganizations] = useState<Option[]>([]);

  const organizationIds =
    selectedOrganizations.length > 0
      ? selectedOrganizations.map((o) => o.value as number)
      : undefined;

  const navigateToSettings = () => {
    navigate({ to: '/account/settings' });
  };

  return (
    <div className='flex h-full flex-col'>
      <ContractViewSheet />
      {/* ── Page header ─────────────────────────────────── */}

      <div className='flex flex-row justify-between py-4'>
        <div className='ml-6 flex flex-col'>
          <h1 className='text-xl font-bold tracking-tight'>{'Λογαρισμός'}</h1>
        </div>
        <div className='mx-2 flex flex-row items-center space-x-4'>
          <OrganizationFilter selected={selectedOrganizations} onApply={setSelectedOrganizations} />
          <Button className='flex items-center gap-3' onClick={navigateToSettings} size='default'>
            <Settings className='h-6 w-6' />
            {toCapitalize(t('settings', { ns: 'common' }))}
          </Button>
        </div>
      </div>

      {/* ── Tabs ────────────────────────────────────────── */}
      <Tabs defaultValue='month' className='flex flex-1 flex-col'>
        <div className='border-b px-6'>
          <TabsList className='h-auto gap-4 bg-transparent p-0'>
            <TabsTrigger
              value='month'
              className='gap-1.5 rounded-none border-b-2 border-transparent px-1 pb-3 pt-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
            >
              <CalendarDays className='h-4 w-4' />
              {t('account:tab_month')}
            </TabsTrigger>
            <TabsTrigger
              value='year'
              className='gap-1.5 rounded-none border-b-2 border-transparent px-1 pb-3 pt-3 text-sm data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:shadow-none'
            >
              <TrendingUp className='h-4 w-4' />
              {t('account:tab_year')}
            </TabsTrigger>
          </TabsList>
        </div>

        <div className='flex-1 overflow-y-auto'>
          <TabsContent value='month' className='mt-0 h-full'>
            <MonthTab organizationIds={organizationIds} />
          </TabsContent>
          <TabsContent value='year' className='mt-0 h-full'>
            <YearTab organizationIds={organizationIds} />
          </TabsContent>
        </div>
      </Tabs>
    </div>
  );
}
